import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getPayment, sendInvoiceMails } from "../../../data";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { AppLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import {
  downloadAllInvoices,
  sendReminderInvoiceMails,
  getReminderMailPreview,
} from "@/data/requests/payment-requests";
import {
  Receipt,
  FileText,
  MapPin,
  Tag,
  DollarSign,
  Mail,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Users,
  Download,
  Send,
  User,
  Clock,
  Eye,
  Loader2,
} from "lucide-react";
import ReactQuill from "react-quill";
import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export function PaymentDetailScreen() {
  const queryClient = useQueryClient();
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ["payment", id],
    queryFn: async () => await getPayment(id),
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm();

  const mutation = useMutation({
    mutationFn: async ({ paymentId }) => {
      await sendInvoiceMails(paymentId);
    },
    onSuccess: () => {
      toast({
        title: "Invoice mails sent successfully",
        type: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["payment", id] });
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || error.message || "Mail gönderimi başarısız";
      toast({
        title: "Mail Gönderimi Başarısız",
        description: errorMessage,
        type: "error",
      });
    },
  });

  const [sendMailsDialogOpen, setSendMailsDialogOpen] = useState(false);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderPreview, setReminderPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const loadReminderPreview = async () => {
    setIsLoadingPreview(true);
    try {
      const response = await getReminderMailPreview(id);
      setReminderPreview(response.data);
    } catch (error) {
      console.error("Error loading reminder preview:", error);
      toast({
        title: "Error loading preview",
        message: error.message,
        type: "error",
      });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleReminderDialogOpen = (open) => {
    setReminderDialogOpen(open);
    if (open) {
      loadReminderPreview();
    } else {
      setReminderPreview(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getEmailTypeLabel = (type) => {
    const labels = {
      INVOICE_REQUEST: "Fatura Talebi",
      REMINDER: "Hatırlatma",
      CHANGE_REQUEST: "Değişiklik Talebi",
      CONFIRMATION: "Onay",
      CUSTOM: "Özel",
    };
    return labels[type] || type;
  };

  const mutationReminder = useMutation({
    mutationFn: async (data) => {
      await sendReminderInvoiceMails(data);
    },
    onSuccess: () => {
      toast({
        title: "Hatırlatma mailleri başarıyla gönderildi",
        type: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["payment", id] });
      setReminderDialogOpen(false);
      setReminderPreview(null);
    },
    onError: (error) => {
      toast({
        title: "Mail gönderimi başarısız",
        message: error.message,
        type: "error",
      });
    },
  });

  const pageSettings = {
    title: "Payment Detail",
    actions: [
      {
        name: "Go To List",
        color: "primary",
        icon: "add-icon",
        onClick: () => {
          navigate("/payments");
        },
      },
      {
        name: "Download All PDF",
        color: "primary",
        icon: "add-icon",
        onClick: async () => {
          await downloadAllInvoices(id).then((res) => {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "invoices.zip");
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
          });
        },
      },
      {
        name: "Send Invoice Mails",
        color: "primary",
        icon: "add-icon",
        onClick: () => {
          setSendMailsDialogOpen(true);
        },
      },
    ],
  };

  const sendMails = () => {
    if (data.data.sendedMailCount == data.data.commissionCount) {
      toast({
        title: "Invoice Mails Already Sent",
        message: "",
        type: "error",
      });
      return;
    }
    setSendMailsDialogOpen(false);
    mutation.mutate({ paymentId: id });
  };

  const onSubmit = async () => {
    if (!reminderPreview || reminderPreview.totalCommissionsToRemind === 0) {
      toast({
        title: "Hatırlatma gönderilecek fatura yok",
        message: "Mail Sended durumunda fatura bulunamadı",
        type: "error",
      });
      return;
    }

    mutationReminder.mutate({
      paymentId: id,
      data: {
        mailBodyMessage: getValues("mailBodyMessage")?.trim(),
      },
    });
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div>Loading...</div>
      </AppLayout>
    );
  }

  if (mutation.isPending || mutationReminder.isPending) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <Mail className="h-12 w-12 text-primary mx-auto mb-4 animate-pulse" />
            <p className="text-lg font-medium">Sending emails...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  const statsCards = [
    {
      label: "Commission Count",
      value: data.data.commissionCount,
      icon: Users,
      color: "blue",
      link: `/payments/${id}/commissions`,
      linkText: "View Commissions",
    },
    {
      label: "Sent Mail Count",
      value: data.data.sendedMailCount,
      icon: Mail,
      color: "green",
    },
    {
      label: "Uploaded Invoices",
      value: data.data.uploadedInvoiceCount,
      icon: FileText,
      color: "purple",
      link: `/payments/${id}/invoices`,
      linkText: "View Invoice PDFs",
    },
    {
      label: "Approved Invoices",
      value: data.data.approvedInvoiceCount,
      icon: CheckCircle2,
      color: "emerald",
      link: `/payments/${id}/invoices`,
      linkText: "View Invoice PDFs",
    },
    {
      label: "Accounting",
      value: data.data.accountingCount,
      icon: DollarSign,
      color: "cyan",
      link: `/payments/${id}/invoices`,
      linkText: "View Invoice PDFs",
    },
    {
      label: "LUCA",
      value: data.data.lucaCount,
      icon: Receipt,
      color: "orange",
      link: `/payments/${id}/invoices`,
      linkText: "View Invoice PDFs",
    },
    {
      label: "Paid",
      value: data.data.paidCount,
      icon: CheckCircle2,
      color: "green",
      link: `/payments/${id}/invoices`,
      linkText: "View Invoice PDFs",
    },
    {
      label: "Error Invoices",
      value: data.data.errorInvoiceCount,
      icon: AlertCircle,
      color: "red",
      link: `/payments/${id}/commissions/error-commissions`,
      linkText: "Error Commissions",
    },
  ];

  const financialData = [
    {
      label: "Total Commission Amount",
      value: data.data.totalCommissionAmount,
      icon: DollarSign,
      color: "blue",
    },
    {
      label: "Total Paid Amount",
      value: data.data.totalPaidAmount,
      icon: CheckCircle2,
      color: "green",
    },
    {
      label: "Total Luca Amount",
      value: data.data.totalLucaAmount,
      icon: Receipt,
      color: "orange",
    },
    {
      label: "Total Luca Ready Amount",
      value: data.data.totalLucaReadyAmount,
      icon: TrendingUp,
      color: "emerald",
    },
    {
      label: "Total In Bank Amount",
      value: data.data.totalInBankAmount,
      icon: DollarSign,
      color: "cyan",
    },
  ];

  const colorClasses = {
    blue: "bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700",
    green: "bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700",
    purple: "bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700",
    emerald: "bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700",
    cyan: "bg-cyan-100 dark:bg-cyan-900/50 text-cyan-700 dark:text-cyan-300 border-cyan-300 dark:border-cyan-700",
    orange: "bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700",
    red: "bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700",
  };

  return (
    <AppLayout
      title={pageSettings.title + " - " + data.data.name}
      actions={pageSettings.actions}
      isLoading={isLoading}
    >
      <div className="px-4 py-6 space-y-6">
        {/* Üst kısım: Solda Payment Info, sağda Commission/istatistik kartları */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* Payment Info Card - Sol */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader className="border-b bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Receipt className="h-5 w-5 text-green-600" />
                  Payment Information
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-4">
                  <div>
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      <Receipt className="h-4 w-4" />
                      Name
                    </Label>
                    <Input
                      value={data.data.name}
                      disabled
                      className="mt-1.5 font-medium"
                    />
                  </div>

                  <div>
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      <FileText className="h-4 w-4" />
                      Invoice ID
                    </Label>
                    <Input
                      value={data.data.invoiceId}
                      disabled
                      className="mt-1.5 font-mono"
                    />
                  </div>

                  <div>
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      Country
                    </Label>
                    <Input
                      value={data.data.commissions[0]?.country || "N/A"}
                      disabled
                      className="mt-1.5"
                    />
                  </div>

                  <div>
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      <Tag className="h-4 w-4" />
                      Payment Type
                    </Label>
                    <div className="mt-1.5">
                      <Badge
                        variant="outline"
                        className={
                          data.data.type === "FIXED"
                            ? colorClasses.purple
                            : colorClasses.blue
                        }
                      >
                        {data.data.type || "DEFAULT"}
                      </Badge>
                    </div>
                  </div>

                  <div>
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      <User className="h-4 w-4" />
                      Related User
                    </Label>
                    <Input
                      value={data.data.relatedUser || "N/A"}
                      disabled
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <Dialog open={reminderDialogOpen} onOpenChange={handleReminderDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        variant="destructive"
                        className="w-full md:w-auto"
                      >
                        <Send className="mr-2 h-4 w-4" />
                        Send Reminder Mail
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[900px] max-h-[90vh]">
                      <form onSubmit={handleSubmit(onSubmit)}>
                        <DialogHeader>
                          <DialogTitle>Hatırlatma Maili Gönder</DialogTitle>
                          <DialogDescription>
                            {reminderPreview ? (
                              <span className="font-semibold text-primary">
                                {reminderPreview.totalCommissionsToRemind} faturaya mail gönderilecek
                              </span>
                            ) : (
                              "Yükleniyor..."
                            )}
                          </DialogDescription>
                        </DialogHeader>

                        {/* Preview Table */}
                        {isLoadingPreview ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            <span className="ml-2">Veriler yükleniyor...</span>
                          </div>
                        ) : reminderPreview && reminderPreview.commissions.length > 0 ? (
                          <div className="my-4">
                            <Label className="text-sm font-medium mb-2 block">
                              Mail Gönderilecek Faturalar
                            </Label>
                            <div className="h-[300px] overflow-auto rounded-md border">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>İsim</TableHead>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Tutar</TableHead>
                                    <TableHead>Son Mail Tarihi</TableHead>
                                    <TableHead>Son Mail Tipi</TableHead>
                                    <TableHead>Açılma</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {reminderPreview.commissions.map((commission) => (
                                    <TableRow key={commission.id}>
                                      <TableCell className="font-medium">
                                        {commission.influencerName}
                                      </TableCell>
                                      <TableCell className="text-sm text-muted-foreground">
                                        {commission.email}
                                      </TableCell>
                                      <TableCell>
                                        ${commission.finalAmount?.toFixed(2)}
                                      </TableCell>
                                      <TableCell>
                                        {commission.lastEmail ? (
                                          <div className="flex items-center gap-1 text-sm">
                                            <Clock className="h-3 w-3" />
                                            {formatDate(commission.lastEmail.sentAt || commission.lastEmail.createdAt)}
                                          </div>
                                        ) : (
                                          <span className="text-muted-foreground">-</span>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {commission.lastEmail ? (
                                          <Badge variant="outline" className="text-xs">
                                            {getEmailTypeLabel(commission.lastEmail.type)}
                                          </Badge>
                                        ) : (
                                          <span className="text-muted-foreground">-</span>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {commission.lastEmail && commission.lastEmail.openCount > 0 ? (
                                          <div className="flex items-center gap-1 text-green-600">
                                            <Eye className="h-3 w-3" />
                                            {commission.lastEmail.openCount}x
                                          </div>
                                        ) : (
                                          <span className="text-muted-foreground">-</span>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                            <div className="mt-2 text-xs text-muted-foreground">
                              Toplam: {reminderPreview.commissions.length} fatura
                            </div>
                          </div>
                        ) : reminderPreview && reminderPreview.commissions.length === 0 ? (
                          <div className="py-8 text-center text-muted-foreground">
                            <Mail className="h-12 w-12 mx-auto mb-2 opacity-50" />
                            <p>Hatırlatma maili gönderilecek fatura bulunamadı.</p>
                            <p className="text-sm">Sadece "Mail Sended" durumundaki faturalara hatırlatma gönderilebilir.</p>
                          </div>
                        ) : null}

                        <div className="grid gap-4 py-4">
                          <div className="grid items-center">
                            <Label htmlFor="mailBodyMessage">Mesaj</Label>
                            <ReactQuill
                              theme="snow"
                              onChange={(value) =>
                                setValue("mailBodyMessage", value)
                              }
                              className="mt-2"
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button 
                            type="submit" 
                            disabled={!reminderPreview || reminderPreview.commissions.length === 0}
                          >
                            <Send className="mr-2 h-4 w-4" />
                            Hatırlatma Maili Gönder ({reminderPreview?.totalCommissionsToRemind || 0})
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Commission / Statistics Cards - Sağ */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {statsCards.map((stat, index) => {
                const Icon = stat.icon;
                const isError = stat.color === "red";
                return (
                  <Card
                    key={index}
                    className={`flex flex-col justify-between ${
                      isError ? "bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800" : ""
                    }`}
                  >
                    <CardHeader className="pb-3">
                      <CardDescription
                        className={`text-sm font-medium ${
                          isError ? "text-red-700 dark:text-red-400" : ""
                        }`}
                      >
                        {stat.label}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-3">
                        <div
                          className={`h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0 ${colorClasses[stat.color]}`}
                        >
                          <Icon className="h-6 w-6" />
                        </div>
                        <CardTitle
                          className={`text-4xl ${
                            isError ? "text-red-700 dark:text-red-400" : ""
                          }`}
                        >
                          {stat.value}
                        </CardTitle>
                      </div>
                    </CardContent>
                    {stat.link && (
                      <CardFooter className="pt-4 border-t">
                        <Button
                          variant={isError ? "destructive" : "outline"}
                          className="w-full"
                          asChild
                        >
                          <Link to={stat.link}>{stat.linkText}</Link>
                        </Button>
                      </CardFooter>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        </div>

        {/* Finansal Özet - En alt */}
        <Card>
          <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30">
            <CardTitle className="flex items-center gap-2 text-lg">
              <DollarSign className="h-5 w-5 text-blue-600" />
              Financial Details
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {financialData.map((item, index) => {
                const Icon = item.icon;
                return (
                  <Card key={index} className="border-l-4 border-l-primary">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorClasses[item.color]}`}
                        >
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground truncate">
                            {item.label}
                          </p>
                          <p className="text-xl font-bold mt-1">
                            ${item.value?.toFixed(2) || "0.00"}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Send Invoice Mails Confirmation Dialog */}
      <Dialog open={sendMailsDialogOpen} onOpenChange={setSendMailsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              Mail Gönderimi Onayı
            </DialogTitle>
            <DialogDescription className="text-base pt-4">
              <span className="font-semibold text-yellow-600">⚠️ Dikkat!</span>
              <br />
              <br />
              Mail göndermeden önce <strong>commission listesinden tutarları kontrol etmeyi unutmayın!</strong>
              <br />
              <br />
              Devam etmek istiyor musunuz?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setSendMailsDialogOpen(false)}
            >
              İptal
            </Button>
            <Button onClick={sendMails}>
              <Send className="h-4 w-4 mr-2" />
              Tamam, Mailleri Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
